import { useEffect, useState } from "react";
import { ClipboardList, Trash2, MessageCircle, FileDown, Users, Receipt } from "lucide-react";
import { api, downloadFileBlob, openFileBlob, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PAYMENT_LABEL = { deposit: "Deposit (RM500)", full: "Bayaran Penuh (RM1,100)" };
const METHOD_LABEL = { bank_transfer: "Bank Transfer", billplz: "Billplz" };
const BILLPLZ_STATUS_STYLE = { pending: "bg-yellow-100 text-yellow-800", paid: "bg-green-100 text-green-800", failed: "bg-red-100 text-red-700" };
const BILLPLZ_STATUS_LABEL = { pending: "Menunggu Bayaran", paid: "Telah Dibayar", failed: "Gagal/Belum Bayar" };

export default function KursusLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = () => api.get("/course-leads").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/course-leads/${id}`);
    toast.success("Pendaftaran dipadam");
    load();
  };

  const downloadOne = async (lead) => {
    setDownloadingId(lead.id);
    try {
      await downloadFileBlob(`/course-leads/${lead.id}/pdf`, `kursus-${lead.full_name.replace(/\s+/g, "-")}.pdf`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadAll = async () => {
    setDownloadingAll(true);
    try {
      await downloadFileBlob("/course-leads/pdf", "senarai-pendaftaran-kursus.pdf");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <div data-testid="admin-kursus-leads-page">
      <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
        <h1 className="font-display text-2xl font-bold text-piks-black">Pendaftaran Kursus</h1>
        {items.length > 0 && (
          <Button size="sm" variant="outline" onClick={downloadAll} disabled={downloadingAll} data-testid="kursus-leads-download-all-btn">
            <Users className="w-3.5 h-3.5 mr-1.5" /> {downloadingAll ? "Menjana..." : "Cetak Semua PDF"}
          </Button>
        )}
      </div>
      <p className="text-sm text-piks-muted mb-6">Senarai minat pendaftaran dari laman /kursus (Kursus Asas Pengawal Peribadi).</p>

      {loading ? (
        <p className="text-piks-muted text-sm">Memuatkan...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-piks-muted bg-white rounded-xl border border-piks-border" data-testid="admin-kursus-leads-empty-state">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" /> Tiada pendaftaran lagi.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} className="p-5 rounded-xl bg-white border border-piks-border flex items-start justify-between gap-4" data-testid={`admin-kursus-lead-${l.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-piks-black">{l.full_name} <span className="text-xs text-piks-muted">({l.ic_number}, {l.gender})</span></p>
                  <span className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-piks-gold/20 text-piks-black">{PAYMENT_LABEL[l.payment_option] || "Deposit (RM500)"}</span>
                  <span className="text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-piks-black/10 text-piks-black">{METHOD_LABEL[l.payment_method] || "Bank Transfer"}</span>
                  {l.payment_method === "billplz" && (
                    <span className={`text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full ${BILLPLZ_STATUS_STYLE[l.billplz_payment_status] || BILLPLZ_STATUS_STYLE.pending}`}>
                      {BILLPLZ_STATUS_LABEL[l.billplz_payment_status] || "Menunggu Bayaran"}
                    </span>
                  )}
                </div>
                <p className="text-sm text-piks-muted mt-1">{l.phone} &middot; {l.location} &middot; {l.current_occupation || "-"}</p>
                <p className="text-xs text-piks-muted mt-1">Pernah ikut kursus keselamatan: {l.prior_training}</p>
                <p className="text-xs text-piks-muted mt-1">Waris: {l.next_of_kin_name || "-"} ({l.next_of_kin_phone || "-"})</p>
                {l.notes && <p className="text-xs text-piks-muted mt-1 italic">"{l.notes}"</p>}
                <p className="text-[11px] text-piks-muted mt-2 uppercase tracking-wide">{new Date(l.created_at).toLocaleString("ms-MY")}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {l.payment_receipt_file_id && (
                  <button onClick={() => openFileBlob(`/files/${l.payment_receipt_file_id}`)} data-testid={`admin-kursus-lead-receipt-${l.id}`} className="text-piks-muted hover:text-piks-black" title="Lihat Resit">
                    <Receipt className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => downloadOne(l)} disabled={downloadingId === l.id} data-testid={`admin-kursus-lead-pdf-${l.id}`} className="text-piks-muted hover:text-piks-black" title="Cetak PDF">
                  <FileDown className="w-4 h-4" />
                </button>
                <a href={`https://wa.me/${l.phone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid={`admin-kursus-lead-whatsapp-${l.id}`} className="text-green-600 hover:text-green-700">
                  <MessageCircle className="w-4 h-4" />
                </a>
                <button onClick={() => remove(l.id)} data-testid={`admin-kursus-lead-delete-${l.id}`} className="text-piks-muted hover:text-piks-red">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
